/**
 * Names for the ids a screen holds (an audit row's project, a budget's machine), without loading every
 * project and machine to find them: `GET /people/v1/names` takes up to 200 ids of each kind and answers
 * only what the caller may see. What was asked is kept for the session, so a refresh asks only for new ids.
 */

import { useEffect, useMemo, useState } from 'react';

import { api } from './api/client.ts';

export interface NamedMachine {
  name: string;
  kind: 'desktop' | 'node';
}

const projectNames = new Map<string, string>();
const machineNames = new Map<string, NamedMachine>();
const projectAreaIds = new Map<string, string>();
/** Ids already asked for (found or not): not asked again this session. */
const asked = new Set<string>();
const listeners = new Set<() => void>();

const batches = <T,>(items: T[], size: number): T[][] => Array.from({ length: Math.ceil(items.length / size) }, (_, i) => items.slice(i * size, (i + 1) * size));

async function resolve(projects: string[], devices: string[]): Promise<void> {
  const wantP = projects.filter((id) => !asked.has(`p:${id}`));
  const wantD = devices.filter((id) => !asked.has(`d:${id}`));
  if (!wantP.length && !wantD.length) return;
  for (const id of wantP) asked.add(`p:${id}`);
  for (const id of wantD) asked.add(`d:${id}`);
  const pb = batches(wantP, 200);
  const db = batches(wantD, 200);
  for (let i = 0; i < Math.max(pb.length, db.length); i++) {
    const qs = new URLSearchParams();
    if (pb[i]?.length) qs.set('projects', pb[i]!.join(','));
    if (db[i]?.length) qs.set('devices', db[i]!.join(','));
    try {
      const a = await api<{ projects: Record<string, string>; projectAreas?: Record<string, string>; devices: Record<string, NamedMachine> }>(`/people/v1/names?${qs}`);
      for (const [id, n] of Object.entries(a.projects)) projectNames.set(id, n);
      for (const [id, n] of Object.entries(a.projectAreas ?? {})) projectAreaIds.set(id, n);
      for (const [id, n] of Object.entries(a.devices)) machineNames.set(id, n);
    } catch {
      // A name that could not be read is an id on screen, which is what it was before; ask again next time.
      for (const id of pb[i] ?? []) asked.delete(`p:${id}`);
      for (const id of db[i] ?? []) asked.delete(`d:${id}`);
    }
  }
  for (const l of listeners) l();
}

/** Names for these project and machine ids; an id with no name (not visible, or removed) is simply absent. */
export function useNames(projectIds: Iterable<string | undefined>, deviceIds: Iterable<string | undefined> = []): { projects: ReadonlyMap<string, string>; projectAreas: ReadonlyMap<string, string>; devices: ReadonlyMap<string, NamedMachine> } {
  const [version, setVersion] = useState(0);
  const p = [...new Set([...projectIds].filter((x): x is string => Boolean(x)))].sort();
  const d = [...new Set([...deviceIds].filter((x): x is string => Boolean(x)))].sort();
  const key = `${p.join(',')}|${d.join(',')}`;
  useEffect(() => {
    const bump = () => setVersion((v) => v + 1);
    listeners.add(bump);
    void resolve(p, d);
    return () => {
      listeners.delete(bump);
    };
    // `key` is the ids: a new set asks again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return useMemo(() => ({ projects: new Map(projectNames), projectAreas: new Map(projectAreaIds), devices: new Map(machineNames) }), [version, key]); // eslint-disable-line react-hooks/exhaustive-deps
}
